/**
 * 身份 store —— 09 章 §9.10.1。
 *
 * 三条纪律：
 * 1. **`perms` 一律来自 `/me`，前端不按 role 推导。** `ROLE_PERMS`
 *    （后端 `core/permissions.py:194`）是唯一权威。推导就是造第二个真相源。
 * 2. `fetchMe` 必须**单飞**。路由守卫、菜单、页面组件可能同时触发它，
 *    不单飞就会在刷新页面时打三次 `/me`。
 * 3. 登出要清干净：token、store、所有 SSE 连接、所有 poller。
 *    漏一个的症状是"退出登录后控制台持续刷 401"。
 */

import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'

import * as authApi from '@/api/auth'
import { tokenStore } from '@/api/client'
// 只在 `reset()` 里用。`stores/menu` 不反向依赖本文件，不成环。
import { useMenuStore } from '@/stores/menu'
import type { Me, Role, Tenant, User } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  const me = shallowRef<Me | null>(null)
  const loading = ref(false)
  /** 已经成功拉过一次 `/me`。路由守卫用它决定要不要等。 */
  const loaded = ref(false)

  let inflight: Promise<Me> | null = null

  const token = computed(() => tokenStore.access)
  const user = computed<User | null>(() => me.value?.user ?? null)
  const tenant = computed<Tenant | null>(() => me.value?.tenant ?? null)
  const tenants = computed<Tenant[]>(() => me.value?.tenants ?? [])
  const role = computed<Role | null>(() => me.value?.role ?? null)
  /**
   * 角色的中文名，由后端从 `roles.label_zh` 带出来。
   *
   * 不要在前端按 `role` 查一张硬编码映射表 —— 自定义角色（迁移 0019）的码是
   * `R0001` 这种序列值，映射表里永远没有它，界面上就会直接露出角色码。
   */
  const roleLabel = computed<string | null>(() => me.value?.role_label ?? null)
  const isSuperadmin = computed(() => me.value?.is_superadmin ?? false)
  /**
   * 我是不是**超级管理员租户**的成员。
   *
   * 与 `isSuperadmin` 是两件事：`is_superadmin` 是平台级标识，而这一位问的是
   * 「我有没有落脚在某个 `tenants.is_admin` 的租户里」。加减超管这个动作要求两者
   * 同时成立（用户 2026-09-10 的口径：能动这个标识的是「超管租户里的超管用户」），
   * 后端判据在 `services/user.is_admin_tenant_member`，越界时 403
   * `ACTOR_NOT_IN_ADMIN_TENANT`。
   *
   * 判据是 `tenants`（我能进的全部）而不是 `tenant`（当前这一个）：一个人可以
   * 同时属于超管租户与别的租户，此刻切在哪个租户里不改变他有没有这个资格。
   */
  const inAdminTenant = computed(() => tenants.value.some((t) => t.is_admin))

  /** 首登强制改密。**必须读顶层字段**——后端在 `MeOut` 顶层与 `user` 里都放了
   *  一份，顶层那份是给这个拦截用的。 */
  const mustChangePassword = computed(() => me.value?.must_change_password ?? false)

  /** 权限集合。`Set` 而不是数组：菜单过滤会对每个节点做一次 `has`，
   *  节点数 ~40、权限数 ~30，数组的 `includes` 是 O(n·m)。 */
  const perms = computed(() => new Set(me.value?.perms ?? []))

  async function fetchMe(force = false): Promise<Me> {
    if (me.value && loaded.value && !force) return me.value
    if (inflight) return inflight

    loading.value = true
    inflight = authApi
      .fetchMe()
      .then((data) => {
        me.value = data
        loaded.value = true
        return data
      })
      .finally(() => {
        loading.value = false
        inflight = null
      })
    return inflight
  }

  /** @param persist 记住登录：true 存 localStorage（跨浏览器会话），false 存 sessionStorage。 */
  async function login(username: string, password: string, persist = true): Promise<Me> {
    const pair = await authApi.login(username, password)
    tokenStore.set(pair, persist)
    // 登录成功后**必须**再拉 `/me`：`TokenPairOut` 恒为三键，
    // 里面没有 role、没有 must_change_password。身份信息的唯一出处是 `/me`。
    return fetchMe(true)
  }

  /** 本地清理。不发请求——供 401 拦截器与跨标签同步调用。 */
  function reset(): void {
    me.value = null
    loaded.value = false
    inflight = null
    tokenStore.clear()
    // 菜单树是身份的派生物，必须跟着一起清。
    //
    //   `menuStore.ensureLoaded()` 开头是 `if (loaded || failed) return`，
    //   不清的话退出再登录**根本不会重新拉** `/menus/me` —— 只有整页刷新
    //   （store 随进程重建）才会。症状是「在菜单管理里改了排序和名字，
    //   退出登录好几次侧栏才变」，而那几次里真正起作用的是某一次整页刷新。
    //
    //   换人登录时后果更重：上一个人的菜单会留在侧栏上。路由本身有
    //   `meta.perms` 兜底（guards.ts 会拦），所以不是越权，但看得见点不动。
    useMenuStore().reset()
  }

  async function logout(): Promise<void> {
    try {
      // 让后端记审计（`auth.logout`）。失败不阻塞本地清理——
      // 网络断了也必须能退出登录。
      await authApi.logout()
    } catch {
      /* 忽略 */
    } finally {
      reset()
    }
  }

  return {
    me,
    loading,
    loaded,
    token,
    user,
    tenant,
    tenants,
    role,
    roleLabel,
    isSuperadmin,
    inAdminTenant,
    mustChangePassword,
    perms,
    fetchMe,
    login,
    logout,
    reset,
  }
})
